import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Project {
  id: string;
  project_name: string;
  project_description: string;
  first_name: string;
  last_name: string;
  created_at: string;
}

export default function ProjectsCarousel() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (projects.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % projects.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [projects.length]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('registrations')
        .select('id, project_name, project_description, first_name, last_name, created_at')
        .eq('payment_status', 'completed')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      if (data && data.length > 0) {
        setProjects(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des projets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || projects.length === 0) {
    return null;
  }

  const currentProject = projects[currentIndex];

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white px-4 sm:px-6 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 rounded-full mb-4"
          >
            <i className="ri-lightbulb-line text-secondary"></i>
            <span className="text-secondary text-sm font-semibold">Projets participants</span>
          </motion.div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Découvrez les projets
          </h2>
          <p className="text-lg text-gray-600">
            {projects.length} projet{projects.length > 1 ? 's' : ''} en compétition
          </p>
        </div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentProject.id}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-10"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-secondary to-primary rounded-2xl flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-3xl text-white"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 break-words">
                    {currentProject.project_name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Par {currentProject.first_name} {currentProject.last_name}
                  </p>
                </div>
              </div>

              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">
                {currentProject.project_description}
              </p>

              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <div className="flex gap-2">
                  {projects.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`h-2 rounded-full transition-all cursor-pointer ${
                        index === currentIndex
                          ? 'w-8 bg-secondary'
                          : 'w-2 bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Aller au projet ${index + 1}`}
                    />
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-all cursor-pointer"
                    aria-label="Projet précédent"
                  >
                    <i className="ri-arrow-left-s-line text-xl text-gray-700"></i>
                  </button>
                  <button
                    onClick={() => setCurrentIndex((prev) => (prev + 1) % projects.length)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-all cursor-pointer"
                    aria-label="Projet suivant"
                  >
                    <i className="ri-arrow-right-s-line text-xl text-gray-700"></i>
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/tous-les-projets')}
            className="inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all cursor-pointer"
          >
            Voir tous les projets
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
